// WhatsAppOptInModal.tsx
// Lets any existing user add/change their WhatsApp number and toggle opt-in.
// Opened via Settings dropdown → "WhatsApp Notifications" menu item.

import React, { useState, useEffect } from 'react';
import {
  Modal, ModalDialog, ModalClose, Typography, Box, Input, Button,
  Switch, Alert, CircularProgress, Divider, Chip
} from '@mui/joy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { supabase } from '../utils/supabaseClient';

interface Props {
  open: boolean;
  userId: string;
  onClose: () => void;
  /** Called after a successful save so parent can refresh state if needed */
  onSaved?: (phone: string | null, optedIn: boolean) => void;
}

export default function WhatsAppOptInModal({ open, userId, onClose, onSaved }: Props) {
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('IN');
  const [optedIn, setOptedIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // OTP verification
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [verifiedPhone, setVerifiedPhone] = useState(''); // full number that passed OTP

  const COUNTRY_PREFIXES: Record<string, string> = {
    IN: '91', US: '1', GB: '44', CA: '1',
    AU: '61', NZ: '64', SG: '65', AE: '971', MY: '60', ZA: '27', OTHER: ''
  };

  /** Split a stored full number like "919876543210" into prefix + local digits */
  function splitStoredNumber(full: string): { code: string; local: string } {
    const digits = full.replace(/\D/g, '');
    for (const [code, prefix] of Object.entries(COUNTRY_PREFIXES)) {
      if (prefix && digits.startsWith(prefix)) {
        return { code, local: digits.slice(prefix.length) };
      }
    }
    return { code: 'OTHER', local: digits };
  }

  // Load current settings when modal opens
  useEffect(() => {
    if (!open || !userId) return;
    setSaved(false);
    setError(null);
    setFetching(true);
    supabase
      .from('profiles')
      .select('whatsapp_phone, whatsapp_opted_in')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data, error: err }) => {
        setFetching(false);
        if (err || !data) return;
        const stored = data.whatsapp_phone ?? '';
        if (stored) {
          const { code, local } = splitStoredNumber(stored);
          setCountryCode(code);
          setPhone(local);
          // Number already verified if user was previously opted-in
          if (data.whatsapp_opted_in) {
            setOtpVerified(true);
            setVerifiedPhone(stored);
          }
        } else {
          setCountryCode('IN');
          setPhone('');
        }
        setOptedIn(data.whatsapp_opted_in ?? true);
      });
  }, [open, userId]);

  function isValidPhone(p: string): boolean {
    const clean = p.replace(/\D/g, '');
    return clean.length >= 7 && clean.length <= 12;
  }

  async function handleSave() {
    setError(null);
    const localDigits = phone.replace(/\D/g, '');

    if (optedIn && !isValidPhone(localDigits)) {
      setError('Please enter a valid mobile number (7–12 digits, without country code).');
      return;
    }

    if (optedIn && !otpVerified) {
      setError('Please verify your WhatsApp number first by sending and entering the OTP.');
      return;
    }

    const prefix = COUNTRY_PREFIXES[countryCode] ?? '';
    const fullNumber = optedIn ? `${prefix}${localDigits}` : null;

    setLoading(true);
    const update: Record<string, unknown> = {
      whatsapp_opted_in: optedIn,
      whatsapp_phone: fullNumber,
      whatsapp_opted_in_at: optedIn ? new Date().toISOString() : null,
    };

    const { error: saveErr } = await supabase
      .from('profiles')
      .update(update)
      .eq('id', userId);

    setLoading(false);
    if (saveErr) {
      setError(saveErr.message);
      return;
    }

    setSaved(true);
    onSaved?.(fullNumber, optedIn);
    // Auto-close after a moment
    setTimeout(onClose, 1500);
  }

  function handlePhoneChange(e: React.ChangeEvent<HTMLInputElement>) {
    setPhone(e.target.value.replace(/\D/g, ''));
    setSaved(false);
    setError(null);
    // Changing the number requires re-verification
    setOtpVerified(false);
    setOtpSent(false);
    setOtpCode('');
    setOtpError(null);
  }

  async function handleSendOtp() {
    const prefix = COUNTRY_PREFIXES[countryCode] ?? '';
    const fullNumber = `${prefix}${phone.replace(/\D/g, '')}`;
    if (!isValidPhone(phone)) {
      setOtpError('Please enter a valid phone number first.');
      return;
    }
    setOtpLoading(true);
    setOtpError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('whatsapp-otp', {
        body: { action: 'send', phone: fullNumber },
      });
      if (fnErr || data?.error) {
        setOtpError(data?.error ?? fnErr?.message ?? 'Failed to send OTP. Please try again.');
      } else {
        setOtpSent(true);
        setOtpCode('');
      }
    } catch (e: any) {
      setOtpError(e.message ?? 'Failed to send OTP.');
    }
    setOtpLoading(false);
  }

  async function handleVerifyOtp() {
    const prefix = COUNTRY_PREFIXES[countryCode] ?? '';
    const fullNumber = `${prefix}${phone.replace(/\D/g, '')}`;
    setOtpLoading(true);
    setOtpError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('whatsapp-otp', {
        body: { action: 'verify', phone: fullNumber, code: otpCode },
      });
      if (fnErr || data?.error) {
        setOtpError(data?.error ?? fnErr?.message ?? 'Verification failed.');
      } else if (data?.verified) {
        setOtpVerified(true);
        setVerifiedPhone(fullNumber);
        setOtpError(null);
      } else {
        setOtpError(data?.error ?? 'Incorrect code. Please try again.');
      }
    } catch (e: any) {
      setOtpError(e.message ?? 'Verification failed.');
    }
    setOtpLoading(false);
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          maxWidth: 440,
          width: '90vw',
          borderRadius: 'lg',
          p: 3,
        }}
      >
        <ModalClose />

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <WhatsAppIcon sx={{ color: '#fff', fontSize: '1.4rem' }} />
          </Box>
          <Box>
            <Typography level="title-md" sx={{ fontWeight: 700 }}>
              WhatsApp Notifications
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
              Free · No spam · Unsubscribe anytime
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Why enable — value proposition */}
        {!fetching && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 'md',
              background: 'linear-gradient(135deg, rgba(37,211,102,0.08) 0%, rgba(18,140,126,0.08) 100%)',
              border: '1px solid',
              borderColor: 'success.outlinedBorder',
            }}
          >
            <Typography level="body-xs" sx={{ fontWeight: 700, color: 'success.700', mb: 0.75 }}>
              Why connect your WhatsApp?
            </Typography>
            {[
              { icon: '📅', text: 'Weekly Vedic forecast every Sunday morning — personalised to your chart' },
              { icon: '🪐', text: 'Dasha change alerts when your planetary period shifts (major life turning points)' },
              { icon: '⚠️', text: 'Important transit alerts when Saturn, Jupiter or Rahu affect your key houses' },
              { icon: '🎯', text: 'Timely reminders for auspicious windows flagged by your feed' },
            ].map(({ icon, text }) => (
              <Box key={text} sx={{ display: 'flex', gap: 0.75, mb: 0.5 }}>
                <Typography level="body-xs" sx={{ flexShrink: 0 }}>{icon}</Typography>
                <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>{text}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {fetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size="sm" />
          </Box>
        ) : (
          <>
            {/* Opt-in toggle */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                p: 1.5,
                borderRadius: 'md',
                bgcolor: optedIn ? 'success.softBg' : 'neutral.softBg',
              }}
            >
              <Box>
                <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                  {optedIn ? '✅ Notifications enabled' : '🔕 Notifications disabled'}
                </Typography>
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  Receive weekly digests &amp; dasha alerts
                </Typography>
              </Box>
              <Switch
                checked={optedIn}
                onChange={(e) => { setOptedIn(e.target.checked); setSaved(false); setError(null); }}
                color={optedIn ? 'success' : 'neutral'}
                size="lg"
              />
            </Box>

            {/* Phone number input + OTP flow */}
            {optedIn && (
              <Box sx={{ mb: 2 }}>
                <Typography level="body-sm" sx={{ mb: 0.75, fontWeight: 500 }}>
                  WhatsApp mobile number
                </Typography>

                {/* Verified state — show locked number with a Change button */}
                {otpVerified ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, borderRadius: 'sm', bgcolor: 'success.softBg', border: '1px solid', borderColor: 'success.outlinedBorder' }}>
                    <Typography level="body-sm" sx={{ flex: 1, color: 'success.700', fontWeight: 600 }}>
                      ✅ Verified: +{COUNTRY_PREFIXES[countryCode]}{phone}
                    </Typography>
                    <Button
                      size="sm"
                      variant="plain"
                      color="neutral"
                      onClick={() => { setOtpVerified(false); setOtpSent(false); setOtpCode(''); setOtpError(null); }}
                    >
                      Change
                    </Button>
                  </Box>
                ) : (
                  <>
                    {/* Number input */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <select
                        value={countryCode}
                        onChange={e => { setCountryCode(e.target.value); setOtpVerified(false); setOtpSent(false); setSaved(false); setError(null); }}
                        disabled={loading || otpLoading}
                        style={{ padding: '8px 6px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff', minWidth: 100, cursor: 'pointer' }}
                      >
                        <option value="IN">🇮🇳 +91</option>
                        <option value="US">🇺🇸 +1</option>
                        <option value="GB">🇬🇧 +44</option>
                        <option value="CA">🇨🇦 +1</option>
                        <option value="AU">🇦🇺 +61</option>
                        <option value="NZ">🇳🇿 +64</option>
                        <option value="SG">🇸🇬 +65</option>
                        <option value="AE">🇦🇪 +971</option>
                        <option value="MY">🇲🇾 +60</option>
                        <option value="ZA">🇿🇦 +27</option>
                        <option value="OTHER">🌍 Other</option>
                      </select>
                      <Input
                        placeholder="10-digit number"
                        value={phone}
                        onChange={handlePhoneChange}
                        inputMode="numeric"
                        slotProps={{ input: { maxLength: 12 } }}
                        startDecorator={
                          <WhatsAppIcon sx={{ color: '#25D366', fontSize: '1rem' }} />
                        }
                        sx={{ '--Input-focusedThickness': '2px', flex: 1 }}
                        disabled={loading || otpLoading}
                      />
                    </Box>
                    <Typography level="body-xs" sx={{ mb: 1, color: 'text.tertiary' }}>
                      Enter number without country code — e.g. 9876543210 for India
                    </Typography>

                    {/* OTP flow */}
                    {!otpSent ? (
                      <Button
                        fullWidth
                        variant="soft"
                        color="success"
                        loading={otpLoading}
                        onClick={handleSendOtp}
                        disabled={!isValidPhone(phone)}
                        sx={{ fontSize: '0.8rem' }}
                      >
                        📲 Send OTP to verify WhatsApp
                      </Button>
                    ) : (
                      <Box>
                        <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                          <Input
                            placeholder="6-digit code"
                            value={otpCode}
                            onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            inputMode="numeric"
                            slotProps={{ input: { maxLength: 6, style: { textAlign: 'center', letterSpacing: 6, fontSize: 18 } } }}
                            sx={{ flex: 1 }}
                            disabled={otpLoading}
                          />
                          <Button
                            variant="solid"
                            color="success"
                            loading={otpLoading}
                            onClick={handleVerifyOtp}
                            disabled={otpCode.length !== 6}
                          >
                            Verify
                          </Button>
                        </Box>
                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                          Didn’t receive it?{' '}
                          <Typography
                            level="body-xs"
                            component="span"
                            sx={{ color: 'success.600', cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={handleSendOtp}
                          >
                            Resend OTP
                          </Typography>
                        </Typography>
                      </Box>
                    )}

                    {otpError && (
                      <Alert color="danger" size="sm" sx={{ mt: 1 }}>{otpError}</Alert>
                    )}
                  </>
                )}
              </Box>
            )}

            {/* What you'll receive */}
            {optedIn && (
              <Box sx={{ mb: 2, p: 1.5, borderRadius: 'md', bgcolor: 'background.level1' }}>
                <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary' }}>
                  Delivery schedule:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {['Sunday forecast', 'Dasha change alerts', 'Transit alerts', 'Muhurta windows'].map(label => (
                    <Chip key={label} size="sm" variant="soft" color="success" sx={{ fontSize: '0.7rem' }}>
                      {label}
                    </Chip>
                  ))}
                </Box>
              </Box>
            )}

            {/* Error */}
            {error && (
              <Alert color="danger" size="sm" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Saved confirmation */}
            {saved && (
              <Alert
                color="success"
                size="sm"
                sx={{ mb: 2 }}
                startDecorator={<CheckCircleIcon fontSize="small" />}
              >
                {optedIn ? 'Saved! You\'ll receive WhatsApp notifications.' : 'WhatsApp notifications disabled.'}
              </Alert>
            )}

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button variant="plain" color="neutral" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                variant="solid"
                color={optedIn ? 'success' : 'neutral'}
                loading={loading}
                onClick={handleSave}
                disabled={loading || (optedIn && !otpVerified)}
                startDecorator={!loading && <WhatsAppIcon sx={{ fontSize: '1rem' }} />}
              >
                {optedIn ? 'Save & Enable' : 'Save'}
              </Button>
            </Box>
          </>
        )}
      </ModalDialog>
    </Modal>
  );
}
